/* @flow */
import idx from 'idx';
import cx from 'classnames';
import * as React from 'react';
import { connect } from 'react-redux';

import { ReactionContainer } from 'chat_v2/components/Reaction';
import CurrentUser from 'auth/CurrentUser';
import DeliveryStatus from 'constants/DeliveryStatus';
import DeliveryStatusIcon from 'chat_v2/components/DeliveryStatusIcon';
import DeliveryStatusLabel from 'chat_v2/components/DeliveryStatusLabel';
import MessageItemBody from './MessageItemBody';
import ReactionButton from 'chat_v2/components/ReactionButton';
import ReactionDisplay from 'chat_v2/components/ReactionDisplay';
import ReactionPickerTrigger from 'chat_v2/components/ReactionPickerTrigger';
import Time from 'components/Time';
import UserAvatar from 'users/components/UserAvatar';
import MessageBodyTranslationSwitcher from './MessageBodyTranslationSwitcher';

import './styles/ChatMessageItem.scss';

type Props = {
  item: *,
  streamUuid: string,
  isLastItemOfChunk: boolean,
  lastPushEventReadSequenceNumber: Number,
};

type State = {
  hovered: boolean,
  reactionPickerVisible: boolean,
  showOriginal: boolean,
};

class ChatMessageItem extends React.Component<Props, State> {
  state: State = {
    hovered: false,
    reactionPickerVisible: false,
    showOriginal: false,
  };

  isAuthor() {
    return CurrentUser.getUserUuid() === this.props.item.item.sender.uuid;
  }

  handleHoverReactionPickerShow = () => {
    this.setState({ reactionPickerVisible: true });
  };

  handleHoverReactionPickerHide = () => {
    this.setState({ reactionPickerVisible: false });
  };

  handleEnterHover = () => {
    this.setState({ hovered: true });
  };

  handleLeaveHover = () => {
    this.setState({ hovered: false });
  };

  shouldShowStatus() {
    return this.props.isLastItemOfChunk && this.isAuthor();
  }

  renderReactions() {
    const { item, streamUuid } = this.props;

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={item.item.uuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => {
          if (!reactionSummary) {
            return this.shouldShowStatus() ? this.renderStatus() : null;
          }

          if (reactionSummary.reactions.length === 0 && this.shouldShowStatus()) {
            return this.renderStatus();
          }

          return (
            <ReactionDisplay
              sentBySelf={this.isAuthor()}
              reactionSummary={reactionSummary}
              addReactionToSummary={addReactionToSummary}
              removeReactionFromSummary={removeReactionFromSummary}
            />
          );
        }}
      </ReactionContainer>
    );
  }

  renderHover() {
    const { streamUuid, item } = this.props;

    return (
      <div className="ChatMessageItem-hover">
        <div className="ChatMessageItem-hover-receipt">
          <Time date={new Date(item.item.createdAt)} hour="numeric" minute="2-digit" />
        </div>
        <div className="ChatMessageItem-hover-reactionButton">
          {item.item.canReact && (
            <ReactionContainer chatUuid={streamUuid} messageUuid={item.item.uuid}>
              {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => {
                if (reactionSummary && reactionSummary.reactions.length >= 1) {
                  return null;
                }

                return (
                  <ReactionPickerTrigger
                    onShow={this.handleHoverReactionPickerShow}
                    onHide={this.handleHoverReactionPickerHide}
                    addReactionToSummary={addReactionToSummary}
                    alignment={this.isAuthor() ? 'left' : 'right'}
                    removeReactionFromSummary={removeReactionFromSummary}>
                    <ReactionButton />
                  </ReactionPickerTrigger>
                );
              }}
            </ReactionContainer>
          )}
        </div>
      </div>
    );
  }

  /* The status may be updated by 3 different ways:
   *
   *   - when we load the message item we display the initial item.readSummary.state
   *   - when we receive a chat message update push event, we update the item in apollo
   *   - when we receive a stream read marker push, we update a redux store and consider
   *     all stream item <= that sequence number to be read.
   *
   */
  renderStatus() {
    let state = idx(this.props, _ => _.item.item.readSummary.state);

    if (!state) {
      return null;
    }

    if (this.props.lastPushEventReadSequenceNumber >= this.props.item.seq) {
      state = DeliveryStatus.READ;
    }

    if (!state) {
      return null;
    }

    return (
      <div className="ChatMessageItem-status">
        <span className="ChatMessageItem-status-label">
          <DeliveryStatusLabel status={state} />
        </span>
        <DeliveryStatusIcon status={state} />
      </div>
    );
  }

  renderAvatar() {
    const { isLastItemOfChunk } = this.props;

    if (!isLastItemOfChunk || this.isAuthor()) {
      return null;
    }

    return (
      <span className="ChatMessageItem-droplet">
        <UserAvatar user={this.props.item.item.sender} size="small" />
      </span>
    );
  }

  hasTranslations() {
    return !this.isAuthor() && this.props.item.item.translationDetails;
  }

  originalLanguageCode() {
    const { translationDetails } = this.props.item.item;
    if (!translationDetails) {
      return null;
    }

    return translationDetails.originalLanguageCode;
  }

  toggleOriginal = () => {
    this.setState({ showOriginal: !this.state.showOriginal });
  };

  renderTranslationSwitcher() {
    const { showOriginal } = this.state;

    return (
      <MessageBodyTranslationSwitcher
        showOriginal={showOriginal}
        originalLanguageCode={this.originalLanguageCode()}
        onToggleShowOriginal={this.toggleOriginal}
      />
    );
  }

  render() {
    const { item } = this.props;
    const { hovered, reactionPickerVisible } = this.state;

    return (
      <div
        className={cx('ChatMessageItem', {
          'ChatMessageItem-self': this.isAuthor(),
          'ChatMessageItem-other': !this.isAuthor(),
          'ChatMessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="ChatMessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}>
          {this.renderHover()}
          <div className="ChatMessageItem-container">
            <MessageItemBody item={this.props.item.item} showOriginal={this.state.showOriginal} />
          </div>
          {this.renderAvatar()}
        </div>
        {item.item.canReact && this.renderReactions()}
        {!item.item.canReact && this.shouldShowStatus() && this.renderStatus()}
        {this.hasTranslations() && this.renderTranslationSwitcher()}
      </div>
    );
  }
}

function mapStateToProps(state: Object, props: Props) {
  return {
    lastPushEventReadSequenceNumber: state.streamReadMarkerPush.get(props.streamUuid) || -1,
  };
}

export { ChatMessageItem };
// $FlowFixMe
export default connect(mapStateToProps, undefined)(ChatMessageItem);
